<template>
  <div class="demo-video" :class="{ 'demo-video--compact': compact, 'demo-video--wide': wide }">
    <div v-if="resolvedTitle || resolvedSubtitle" class="demo-video__intro">
      <h2 v-if="resolvedTitle" class="demo-video__title">{{ resolvedTitle }}</h2>
      <p v-if="resolvedSubtitle" class="demo-video__subtitle">{{ resolvedSubtitle }}</p>
    </div>
    <div class="demo-video__frame">
      <iframe
        v-if="showVideo && isYouTube"
        :src="youtubeEmbedUrl"
        :title="iframeTitle"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowfullscreen
        referrerpolicy="strict-origin-when-cross-origin"
      />
      <video
        v-else-if="showVideo && useNativePlayer"
        class="demo-video__player"
        controls
        playsinline
        preload="metadata"
        :poster="posterUrl"
        :src="videoUrl"
      >
        Sorry, your browser doesn’t support embedded videos.
      </video>
      <iframe
        v-else-if="showVideo"
        :src="embedUrl"
        :title="iframeTitle"
        loading="lazy"
        allow="encrypted-media; fullscreen; microphone; screen-wake-lock;"
        allowfullscreen
        referrerpolicy="strict-origin-when-cross-origin"
      />
      <!-- Consent gate: nothing from YouTube or Synthesia is fetched until
           marketing cookies are accepted or this is clicked. The poster is
           SELF-HOSTED (static/video-posters), so the visitor sees the video's
           own frame instead of a blank navy panel (Muhammad, 18 Sep 2026)
           and no third party is contacted before consent. -->
      <button
        v-else
        type="button"
        class="demo-video__placeholder"
        :class="{ 'demo-video__placeholder--poster': !!posterUrl }"
        :style="posterUrl ? { backgroundImage: 'url(' + posterUrl + ')' } : null"
        @click="loaded = true"
      >
        <span class="demo-video__play" aria-hidden="true">&#9658;</span>
        <span class="demo-video__placeholder-text">Load video</span>
        <span class="demo-video__placeholder-note">
          This video is hosted by {{ hostLabel }} and may set its own cookies.
          Click to load it.
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { eventBus } from '@/utils/eventBus';
import { hasMarketing } from '@/utils/consent';
import { getImproveVideo } from '@/config/videos';

// Synthesia's file for ONE version of the Partner Demo. Editing the video in
// Synthesia makes a new version and withdraws the old file (found 19 Oct 2026:
// version 3 answered 403 and Watch Demo played nothing; version 4 is dated
// 6 Oct): bump the version here when that happens, until the demo moves to
// YouTube.
const DEFAULT_VIDEO_MP4 =
  process.env.VUE_APP_DEMO_VIDEO_MP4 ||
  'https://synthesia-ttv-data.s3-eu-west-1.amazonaws.com/video_data/c3f62933-bc9c-404c-857f-20a38de5bd80/versions/4/transfers/rendered_video.mp4';

// Self-hosted frame of the Partner Demo (Synthesia's own thumbnail URL is a
// third-party request, which the consent gate exists to avoid).
const DEFAULT_POSTER =
  process.env.VUE_APP_DEMO_VIDEO_POSTER || '/static/video-posters/partner-demo.jpg';

const DEFAULT_DEMO_VIDEO =
  process.env.VUE_APP_DEMO_VIDEO_URL ||
  'https://share.synthesia.io/embeds/videos/c3f62933-bc9c-404c-857f-20a38de5bd80';

function extractYouTubeId(value) {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (/^[\w-]{11}$/.test(trimmed)) return trimmed;
  const match = trimmed.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|shorts\/|watch\?v=|live\/))([\w-]{11})/
  );
  return match ? match[1] : '';
}

export default {
  name: 'DemoVideo',
  props: {
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    /** Catalog key from src/config/videos.js (preferred) */
    videoKey: { type: String, default: '' },
    /** YouTube ID or full YouTube URL */
    youtubeId: { type: String, default: '' },
    src: { type: String, default: '' },
    compact: { type: Boolean, default: false },
    /** Full container width (960px). Default is 760px centred: on a section
        page the video sits under the hero and at 960px it dominated the page
        (Steve, 15 Sep 2026). The homepage and Watch Demo, where the video IS
        the content, opt in to wide. */
    wide: { type: Boolean, default: false },
    /** Use third-party iframe (Synthesia) instead of the MP4 player */
    forceEmbed: { type: Boolean, default: false },
    /** Self-hosted poster frame; defaults per catalogue key / the demo */
    poster: { type: String, default: '' },
  },
  data() {
    return {
      loaded: false,
      consentTick: 0,
    };
  },
  computed: {
    catalog() {
      return this.videoKey ? getImproveVideo(this.videoKey) : null;
    },
    resolvedTitle() {
      return this.title || (this.catalog && this.catalog.title) || '';
    },
    resolvedSubtitle() {
      return this.subtitle || (this.catalog && this.catalog.subtitle) || '';
    },
    resolvedYouTubeId() {
      return (
        extractYouTubeId(this.youtubeId) ||
        extractYouTubeId(this.src) ||
        (this.catalog && this.catalog.id) ||
        ''
      );
    },
    isYouTube() {
      return !!this.resolvedYouTubeId;
    },
    youtubeEmbedUrl() {
      return (
        'https://www.youtube-nocookie.com/embed/' +
        this.resolvedYouTubeId +
        '?rel=0&modestbranding=1'
      );
    },
    videoUrl() {
      return this.src || DEFAULT_VIDEO_MP4;
    },
    posterUrl() {
      if (this.poster) return this.poster;
      // Catalogue videos carry a self-hosted frame (videos.js `poster`,
      // fetched once from YouTube and committed, so the placeholder never
      // calls out). A YouTube id given directly has no local frame.
      if (this.catalog) return this.catalog.poster || '';
      if (this.isYouTube) return '';
      return DEFAULT_POSTER;
    },
    embedUrl() {
      return this.src || DEFAULT_DEMO_VIDEO;
    },
    useNativePlayer() {
      return !this.forceEmbed && !this.isYouTube;
    },
    showVideo() {
      return this.loaded || (this.consentTick >= 0 && hasMarketing());
    },
    hostLabel() {
      return this.isYouTube ? 'YouTube' : 'Synthesia';
    },
    iframeTitle() {
      return this.resolvedTitle
        ? this.resolvedTitle + ' - IMPROVE'
        : 'IMPROVE product video';
    },
  },
  created() {
    eventBus.$on('consent-updated', this.onConsentUpdated);
  },
  beforeDestroy() {
    eventBus.$off('consent-updated', this.onConsentUpdated);
  },
  methods: {
    onConsentUpdated() {
      this.consentTick += 1;
    },
  },
};
</script>

<style scoped>
.demo-video {
  width: 100%;
}

.demo-video__intro {
  margin-bottom: 16px;
  text-align: center;
}

.demo-video__title {
  font-family: 'Sora', sans-serif;
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px;
}

.demo-video__subtitle {
  color: var(--brand-grey, #6b7280);
  font-size: 16px;
  margin: 0;
}

.demo-video__frame {
  position: relative;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  aspect-ratio: 1920 / 1080;
  overflow: hidden;
  border-radius: 8px;
  background: var(--brand-navy, #002458);
  box-shadow: 0 12px 32px rgba(0, 36, 88, 0.16);
}

.demo-video__frame iframe,
.demo-video__player {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  padding: 0;
  margin: 0;
  overflow: hidden;
}

.demo-video__player {
  object-fit: contain;
  background: #000;
}

.demo-video__placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  background: var(--brand-navy, #002458);
  color: #ffffff;
  font-family: 'Sora', sans-serif;
}

.demo-video__placeholder--poster {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  /* Legible play button and note over any frame. */
  box-shadow: inset 0 0 0 100vmax rgba(0, 20, 50, 0.45);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
}

.demo-video__play {
  font-size: 42px;
  line-height: 1;
}

.demo-video__placeholder-text {
  font-size: 18px;
  font-weight: 600;
}

.demo-video__placeholder-note {
  font-size: 13px;
  opacity: 0.82;
  max-width: 360px;
  line-height: 1.4;
}

.demo-video__placeholder:focus-visible {
  outline: 2px solid #08a0a8;
  outline-offset: -4px;
}

.demo-video--wide .demo-video__frame {
  max-width: none;
}

.demo-video--compact .demo-video__title {
  font-size: 20px;
}

.demo-video--compact .demo-video__frame {
  box-shadow: 0 6px 18px rgba(0, 36, 88, 0.12);
}
</style>
